import * as Clipboard from 'expo-clipboard';
import type { ComponentProps, ReactNode } from 'react';
import { Share } from 'react-native';
import type ButtonToast from '@/components/Custom/ButtonToast';
import * as Header from '@/components/Custom/Header';
import Card from '@/components/UI/Card';
import Item from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import useIsSharingAvailable from '@/hooks/useIsSharingAvailable';

export default function InviteShareOptions({
  code,
  link,
  disabled,
  testIDPrefix,
  setToast,
  children,
}: {
  code: string | null | undefined;
  link: string;
  disabled: boolean;
  testIDPrefix: string;
  setToast: (toast: ComponentProps<typeof ButtonToast>) => void;
  children?: ReactNode;
}) {
  const sharingAvailable = useIsSharingAvailable();
  const copy = async (value: string, label: string) => {
    await Clipboard.setStringAsync(value);
    setToast({
      text: `Copied Invite ${label}`,
      icon: 'checkmark-outline',
      variant: 'success',
    });
  };
  const share = async (value: string) => {
    try {
      await Share.share({ message: value });
    } catch {
      setToast({
        text: 'Could not share invite',
        icon: 'close-outline',
        variant: 'error',
      });
    }
  };
  return (
    <Box space="xl" p="lg">
      {[
        { label: 'Link', value: link },
        { label: 'Code', value: code ?? '' },
      ].map((option) => (
        <Box key={option.label} space="md">
          <Text bold size="xl">
            Invite with a {option.label.toLowerCase()}
          </Text>
          <Card>
            {option.label === 'Link' ? children : null}
            <Item
              disabled={!code || disabled}
              onPress={() => copy(option.value, option.label)}
            >
              <Header.Button
                accessibilityLabel={`Copy invite ${option.label.toLowerCase()}`}
                disabled={!code || disabled}
                onPress={() => copy(option.value, option.label)}
              >
                <Icon name="copy-outline" />
              </Header.Button>
              {sharingAvailable && (
                <Header.Button
                  accessibilityLabel={`Share invite ${option.label.toLowerCase()}`}
                  disabled={!code || disabled}
                  onPress={() => share(option.value)}
                >
                  <Icon name="share-outline" />
                </Header.Button>
              )}
              <Text
                flexShrink={1}
                numberOfLines={option.label === 'Code' ? 1 : 4}
                selectable
                size={option.label === 'Code' ? '2xl' : 'md'}
                testID={`${testIDPrefix}Invite${option.label}`}
              >
                {option.value}
              </Text>
            </Item>
          </Card>
        </Box>
      ))}
    </Box>
  );
}
